import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { recursiveFind } from '../../../utilities/treeHelpers';
import { resolveEntityApi } from '../billParsingApi';

const useSimilarEntityDetection = (groupedMeters, entityTree, orgId) => {
  const [resolutions, setResolutions] = useState({});
  const [userActions, setUserActions] = useState({});
  const [manualSelections, setManualSelections] = useState({});
  const fetchedRef = useRef(new Set());

  useEffect(() => {
    if (!groupedMeters || !orgId) return;

    const entityNames = Array.from(new Set(groupedMeters.map((g) => g.entityName)));

    entityNames.forEach((entityName) => {
      if (fetchedRef.current.has(entityName)) return;
      fetchedRef.current.add(entityName);

      setResolutions((prev) => ({
        ...prev,
        [entityName]: { loading: true }
      }));

      resolveEntityApi(orgId, entityName)
        .then((result) => {
          setResolutions((prev) => ({
            ...prev,
            [entityName]: {
              loading: false,
              existingEntityId: result.existingEntityId,
              entityName: result.entityName,
              isInactive: result.isInactive,
              matchedByExactName: result.matchedByExactName,
              matchedBySimilarName: result.matchedBySimilarName,
              matchedByMapping: result.matchedByMapping
            }
          }));
        })
        .catch((error) => {
          setResolutions((prev) => ({
            ...prev,
            [entityName]: {
              loading: false,
              error: error.message || error,
              existingEntityId: null,
              matchedByExactName: false,
              matchedBySimilarName: false,
              matchedByMapping: false
            }
          }));
        });
    });
  }, [groupedMeters, orgId]);

  const similarEntityMatches = useMemo(() => {
    const matches = {};
    Object.keys(resolutions).forEach((entityName) => {
      const res = resolutions[entityName];
      if (!res) return;
      if (res.loading) {
        matches[entityName] = { loading: true };
        return;
      }
      if (res.error) {
        matches[entityName] = { error: res.error, existingEntityId: null };
        return;
      }

      if (manualSelections[entityName]) {
        const selectedEntity = manualSelections[entityName];
        matches[entityName] = {
          existingEntityId: selectedEntity.divId,
          entityName: selectedEntity.divName,
          isInactive: selectedEntity.divEnabled !== 'Y',
          matchedByExactName: false,
          matchedBySimilarName: false,
          matchedByMapping: false,
          similarEntity: selectedEntity,
          action: 'keep'
        };
        return;
      }

      const similarEntity = (res.existingEntityId && entityTree)
        ? recursiveFind(entityTree, (node) => node && node.divId === res.existingEntityId)
        : null;

      matches[entityName] = {
        existingEntityId: res.existingEntityId,
        entityName: res.entityName,
        isInactive: res.isInactive,
        matchedByExactName: res.matchedByExactName,
        matchedBySimilarName: res.matchedBySimilarName,
        matchedByMapping: res.matchedByMapping,
        similarEntity,
        action: userActions[entityName] || null
      };
    });
    return matches;
  }, [resolutions, entityTree, userActions, manualSelections]);

  const handleKeepExisting = useCallback((entityName) => {
    setUserActions((prev) => ({
      ...prev,
      [entityName]: 'keep'
    }));
  }, []);

  const handleUseNew = useCallback((entityName) => {
    setUserActions((prev) => ({
      ...prev,
      [entityName]: 'use-new'
    }));
  }, []);

  const renameEntityInMatches = useCallback((oldName, newName) => {
    fetchedRef.current.add(newName);
    fetchedRef.current.delete(oldName);
    setResolutions((prev) => {
      if (!prev[oldName]) return prev;
      const updated = { ...prev };
      updated[newName] = prev[oldName];
      delete updated[oldName];
      return updated;
    });
    setUserActions((prev) => {
      if (!prev[oldName]) return prev;
      const updated = { ...prev };
      updated[newName] = prev[oldName];
      delete updated[oldName];
      return updated;
    });
    setManualSelections((prev) => {
      if (!prev[oldName]) return prev;
      const updated = { ...prev };
      updated[newName] = prev[oldName];
      delete updated[oldName];
      return updated;
    });
  }, []);

  const updateSimilarMatch = useCallback((entityName, selectedEntity) => {
    fetchedRef.current.add(entityName);
    setManualSelections((prev) => ({
      ...prev,
      [entityName]: selectedEntity
    }));
  }, []);

  return {
    similarEntityMatches,
    handleKeepExisting,
    handleUseNew,
    renameEntityInMatches,
    updateSimilarMatch
  };
};

export default useSimilarEntityDetection;
